import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { Navigate, useLocation, useMatch, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authApiClient } from '../../authApiClient';
import { authService } from '../../services/AuthService';
import SEO from '../../components/SEO';
import { isNetworkError } from '../../utils/errorHandler';
import { parseInviteRole, type InviteRole } from '../../utils/inviteRegistration';
import { appendBookingResumeSearchParams, type AuthBookingState } from '../../utils/bookingLoginResume';
import { OrganizationType } from '../../models/Organization';
import ProviderOrganizationTypeToggle, {
  type ProviderOrganizationType,
} from '../../components/ProviderOrganizationTypeToggle';
import { useSubscriptionFeaturesEnabled } from '../../utils/subscriptionFeatures';
import {
  buildSubscriptionOnboardingPath,
  cacheOrganizationType,
  isSubscriptionOnboardingPath,
  markSubscriptionOnboardingActive,
  storeOnboardingAccessToken,
  storeOnboardingDisplayName,
  storeOnboardingOrganizationName,
} from '../../utils/subscriptionOnboarding';
import { notifyOrganizationTypeUpdated } from '../../utils/subscriptionStripeReturn';

/** Open signup only offers musician vs provider; invites may still lock tutor/host/user. */
type RoleType = InviteRole;

type HostOrganizationType =
  | OrganizationType.REHEARSAL_STUDIO
  | OrganizationType.MUSIC_SCHOOL;

interface LocationState {
  accessToken?: string;
  inviteRole?: InviteRole;
  redirectUrl?: string;
  bookingState?: AuthBookingState;
}

function resolvePostAuthRedirect(
  redirectUrl: string | undefined,
  bookingState: LocationState['bookingState'] | undefined,
  fallback: string,
): string {
  if (!redirectUrl?.trim()) {
    return fallback;
  }
  try {
    const url = new URL(redirectUrl, window.location.origin);
    appendBookingResumeSearchParams(url, bookingState);
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return fallback;
  }
}

const ProfileCompletionSelectionPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const inviteCompleteMatch = useMatch('/invite/:role/complete');
  const fixedRoleFromPath = parseInviteRole(inviteCompleteMatch?.params.role);
  const subscriptionFeaturesEnabled = useSubscriptionFeaturesEnabled();
  const locationState = location.state as LocationState | undefined;
  const fixedRole = fixedRoleFromPath ?? parseInviteRole(locationState?.inviteRole);
  const [selectedRole, setSelectedRole] = useState<RoleType | null>(fixedRole ?? 'user');
  const [providerOrganizationType, setProviderOrganizationType] =
    useState<ProviderOrganizationType | null>(null);
  const [username, setUsername] = useState('');
  const [bandName, setBandName] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const accessToken = locationState?.accessToken || localStorage.getItem('access_token') || '';
  const pendingRedirectUrl = locationState?.redirectUrl;
  const pendingBookingState = locationState?.bookingState;

  const roleOptions = useMemo(() => {
    const providerCard = {
      key: 'host' as const,
      title: t('authOnboarding.roles.provider.title', 'I host or teach'),
      description: t(
        'authOnboarding.roles.provider.description',
        'For rehearsal studio hosts, music schools, and individual tutors.',
      ),
      ...(subscriptionFeaturesEnabled
        ? {
            badge: t('authOnboarding.roles.provider.badge', 'Free Trial'),
            badgeColor: 'secondary' as const,
          }
        : {}),
    };

    return [
      {
        key: 'user' as const,
        title: t('authOnboarding.roles.user.title', "I'm a musician"),
        description: t(
          'authOnboarding.roles.user.description',
          subscriptionFeaturesEnabled
            ? 'Book rehearsal rooms and lessons. Search studios and tutors on Band Space.'
            : 'Search rehearsal studios and tutors, then book sessions.',
        ),
        ...(subscriptionFeaturesEnabled
          ? {
              badge: t('authOnboarding.roles.user.badge', 'Free'),
              badgeColor: 'success' as const,
            }
          : {}),
      },
      providerCard,
    ];
  }, [t, subscriptionFeaturesEnabled]);

  const fixedRoleCopy = useMemo(() => {
    if (!fixedRole) return null;
    const descriptionKey =
      fixedRole === 'host'
        ? 'authOnboarding.roles.provider.description'
        : `authOnboarding.roles.${fixedRole}.description`;
    return {
      title: t(`authInvite.${fixedRole}.completeTitle`, defaultCompleteTitle(fixedRole)),
      description: t(descriptionKey, defaultCompleteDescription(fixedRole)),
    };
  }, [fixedRole, t]);

  if (inviteCompleteMatch && !fixedRoleFromPath) {
    return <Navigate to="/complete-profile" replace state={locationState} />;
  }

  const getErrorMessage = (raw: string): string => {
    if (isNetworkError(raw)) return t('auth.networkError');
    return raw || t('auth.unknownError');
  };

  const completeTutorPath = async (trimmedName: string) => {
    await authApiClient.completeTutorProfile({
      accessToken,
      name: trimmedName,
    });
    const refreshed = await authService.refreshSessionAfterProfileCompletion();
    if (!refreshed) {
      setError(
        t(
          'authOnboarding.errors.sessionRefreshFailed',
          'Could not update your session. Please sign in again.',
        ),
      );
      setIsSubmitting(false);
      return;
    }
    const roles = authService.getRoles();
    if (!roles.includes('TUTOR')) {
      setError(
        t(
          'authOnboarding.errors.roleNotConfirmed',
          'Could not confirm tutor access. Please sign in again.',
        ),
      );
      setIsSubmitting(false);
      return;
    }
    cacheOrganizationType(OrganizationType.INDIVIDUAL_TUTOR);
    notifyOrganizationTypeUpdated(OrganizationType.INDIVIDUAL_TUTOR);
    if (subscriptionFeaturesEnabled && roles.includes('HOST')) {
      markSubscriptionOnboardingActive('/tutor/listing');
      navigate(buildSubscriptionOnboardingPath({ next: '/tutor/listing' }), { replace: true });
      return;
    }
    navigate('/tutor/listing', { replace: true });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const roleToSubmit = fixedRole ?? selectedRole;
    const trimmedName = username.trim();
    const trimmedBandName = bandName.trim();
    const trimmedOrganizationName = organizationName.trim();
    const selectingProviderType = roleToSubmit === 'host';

    if (!accessToken) {
      setError(t('authOnboarding.errors.missingToken', 'Authentication expired. Please sign in again.'));
      return;
    }
    if (!roleToSubmit) {
      setError(t('authOnboarding.errors.roleRequired', 'Select your profile type to continue.'));
      return;
    }
    if (!trimmedName) {
      setError(t('authOnboarding.errors.usernameRequired', 'Username is required.'));
      return;
    }
    if (selectingProviderType && !providerOrganizationType) {
      setError(
        t(
          'authOnboarding.errors.organizationTypeRequired',
          'Select music school, rehearsal studio, or individual tutor.',
        ),
      );
      return;
    }

    setIsSubmitting(true);
    try {
      if (roleToSubmit === 'user') {
        await authApiClient.completeUserProfile({
          accessToken,
          name: trimmedName,
          ...(trimmedBandName ? { bandName: trimmedBandName } : {}),
        });
        const refreshed = await authService.refreshSessionAfterProfileCompletion();
        if (!refreshed) {
          setError(t('authOnboarding.errors.sessionRefreshFailed', 'Could not update your session. Please sign in again.'));
          setIsSubmitting(false);
          return;
        }
        if (!authService.getRoles().includes('USER')) {
          setError(t('authOnboarding.errors.roleNotConfirmed', 'Could not confirm your profile. Please sign in again.'));
          setIsSubmitting(false);
          return;
        }
        // Convert already fired on OTP verify insert.
        // Pricing CTA may pass /onboarding/plan as post-auth redirect; musicians stay on search.
        const musicianRedirect = isSubscriptionOnboardingPath(pendingRedirectUrl)
          ? undefined
          : pendingRedirectUrl;
        navigate(
          resolvePostAuthRedirect(musicianRedirect, pendingBookingState, '/user/search'),
          { replace: true },
        );
        return;
      }

      if (roleToSubmit === 'tutor' || providerOrganizationType === OrganizationType.INDIVIDUAL_TUTOR) {
        if (subscriptionFeaturesEnabled) {
          storeOnboardingDisplayName(trimmedName);
          storeOnboardingOrganizationName(trimmedOrganizationName);
        }
        await completeTutorPath(trimmedName);
        return;
      }

      if (!subscriptionFeaturesEnabled) {
        await authApiClient.completeHostProfile({
          accessToken,
          name: trimmedName,
          organizationName: trimmedOrganizationName || null,
          organizationType: providerOrganizationType as HostOrganizationType,
        });
        const refreshed = await authService.refreshSessionAfterProfileCompletion();
        if (!refreshed) {
          setError(t('authOnboarding.errors.sessionRefreshFailed', 'Could not update your session. Please sign in again.'));
          setIsSubmitting(false);
          return;
        }
        if (!authService.getRoles().includes('HOST')) {
          setError(t('authOnboarding.errors.roleNotConfirmed', 'Could not confirm host access. Please sign in again.'));
          setIsSubmitting(false);
          return;
        }
        cacheOrganizationType(providerOrganizationType!);
        notifyOrganizationTypeUpdated(providerOrganizationType!);
        navigate('/host/organization', { replace: true });
        return;
      }

      // Provider / host: defer org creation until a plan is chosen (or user switches to musician).
      storeOnboardingDisplayName(trimmedName);
      storeOnboardingOrganizationName(trimmedOrganizationName);
      authService.syncSessionFromStorage();
      storeOnboardingAccessToken(accessToken);
      cacheOrganizationType(providerOrganizationType!);
      markSubscriptionOnboardingActive();
      navigate(buildSubscriptionOnboardingPath(), { replace: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(getErrorMessage(message));
      setIsSubmitting(false);
    }
  };

  const activeRole = fixedRole ?? selectedRole;
  const showBandNameField = activeRole === 'user';
  const showProviderOrganizationType = activeRole === 'host';
  const showOrganizationNameField = showProviderOrganizationType;
  const orgTypeLabel = (type: ProviderOrganizationType) =>
    t(`subscription.orgTypes.${type}`, type.replace(/_/g, ' '));
  const pageTitle = fixedRoleCopy?.title ?? t('authOnboarding.title', 'Complete your profile');
  const pageSubtitle = fixedRoleCopy?.description
    ?? t(
      'authOnboarding.subtitle',
      'Tell us how you will use Band Space.',
    );

  return (
    <>
      <SEO title={pageTitle} noindex={true} />
      <Container component="main" maxWidth="sm">
        <Box sx={{ mt: 6, mb: 6 }}>
          <Paper elevation={3} sx={{ p: { xs: 2, sm: 4 } }}>
            <Typography variant="h5" gutterBottom>
              {pageTitle}
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              {pageSubtitle}
            </Typography>

            {!fixedRole && (
              <Grid container spacing={2} sx={{ mb: 3, overflow: 'visible' }} alignItems="stretch">
                {roleOptions.map((role) => {
                  const selected = selectedRole === role.key;
                  return (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      key={role.key}
                      sx={{ display: 'flex', overflow: 'visible', pt: role.badge ? 1.5 : 0 }}
                    >
                      <Card
                        variant="outlined"
                        sx={{
                          position: 'relative',
                          overflow: 'visible',
                          borderColor: selected ? 'primary.main' : 'divider',
                          borderWidth: selected ? 2 : 1,
                          boxShadow: 'none',
                          width: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          transition: 'border-color 0.15s ease',
                          '&:hover': {
                            borderColor: selected ? 'primary.main' : 'text.secondary',
                            boxShadow: 'none',
                          },
                        }}
                      >
                        {role.badge ? (
                          <Chip
                            label={role.badge}
                            color={role.badgeColor}
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: -12,
                              left: '50%',
                              transform: 'translateX(-50%)',
                              zIndex: 2,
                            }}
                          />
                        ) : null}
                        <CardActionArea
                          onClick={() => {
                            setSelectedRole(role.key);
                            if (role.key !== 'host') {
                              setProviderOrganizationType(null);
                            }
                          }}
                          disabled={isSubmitting}
                          disableRipple
                          sx={{
                            borderRadius: 'inherit',
                            overflow: 'hidden',
                            flex: 1,
                            display: 'flex',
                            alignItems: 'stretch',
                            '.MuiCardActionArea-focusHighlight': {
                              display: 'none',
                            },
                          }}
                        >
                          <CardContent
                            sx={{
                              flex: 1,
                              pt: role.badge ? 3 : 2,
                              display: 'flex',
                              flexDirection: 'column',
                            }}
                          >
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                              {role.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {role.description}
                            </Typography>
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}

            <Divider sx={{ mb: 3 }} />

            <Box component="form" onSubmit={handleSubmit}>
              {showProviderOrganizationType ? (
                <Box sx={{ mb: 2 }}>
                  <ProviderOrganizationTypeToggle
                    value={providerOrganizationType}
                    onChange={setProviderOrganizationType}
                    labels={{
                      [OrganizationType.MUSIC_SCHOOL]: orgTypeLabel(OrganizationType.MUSIC_SCHOOL),
                      [OrganizationType.REHEARSAL_STUDIO]: orgTypeLabel(OrganizationType.REHEARSAL_STUDIO),
                      [OrganizationType.INDIVIDUAL_TUTOR]: orgTypeLabel(OrganizationType.INDIVIDUAL_TUTOR),
                    }}
                    ariaLabel={t('subscription.organizationTypeLabel', 'Organization type')}
                    disabled={isSubmitting}
                  />
                </Box>
              ) : null}
              <Box sx={{ maxWidth: 460, mx: 'auto', display: 'grid', gap: 2 }}>
                <TextField
                  fullWidth
                  required
                  label={t('signup.username', 'Username')}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={isSubmitting}
                  autoComplete="nickname"
                />
                {showBandNameField ? (
                  <TextField
                    fullWidth
                    label={t('authOnboarding.bandName', 'Band Name (Optional)')}
                    value={bandName}
                    onChange={(e) => setBandName(e.target.value)}
                    disabled={isSubmitting}
                    autoComplete="organization"
                  />
                ) : null}
                {showOrganizationNameField ? (
                  <TextField
                    fullWidth
                    label={t('authOnboarding.organizationName', 'Organization Name (Optional)')}
                    value={organizationName}
                    onChange={(e) => setOrganizationName(e.target.value)}
                    disabled={isSubmitting}
                    autoComplete="organization"
                  />
                ) : null}
              </Box>

              {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}

              <Box
                sx={{
                  mt: 3,
                  display: 'flex',
                  gap: 1.5,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Button
                  variant="contained"
                  type="submit"
                  disabled={isSubmitting || !activeRole}
                  sx={{ minWidth: 140 }}
                >
                  {isSubmitting
                    ? t('common.loading', 'Loading...')
                    : t('common.continue', 'Continue')}
                </Button>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Container>
    </>
  );
};

function defaultCompleteTitle(role: InviteRole): string {
  switch (role) {
    case 'user':
      return 'Complete your musician profile';
    case 'host':
      return 'Complete your host profile';
    case 'tutor':
      return 'Complete your tutor profile';
  }
}

function defaultCompleteDescription(role: InviteRole): string {
  switch (role) {
    case 'user':
      return 'Book rehearsal rooms and lessons. Search studios and tutors on Band Space.';
    case 'host':
      return 'For rehearsal studio hosts, music schools, and individual tutors.';
    case 'tutor':
      return 'Offer tutoring sessions and manage your tutor bookings.';
  }
}

export default ProfileCompletionSelectionPage;
